import { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Typography,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShareIcon from '@mui/icons-material/Share';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import type { Item } from '../../types';
import { hasFlag, ItemFlags } from '../../types';

const typeIcons = {
  url: <LinkIcon fontSize="small" />,
  text: <TextFieldsIcon fontSize="small" />,
  image: <ImageIcon fontSize="small" />,
  file: <AttachFileIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
};

interface ItemCardProps {
  item: Item;
  onClick: (item: Item) => void;
  onEdit?: (item: Item) => void;
  onDelete?: (item: Item) => void;
  onRefresh?: (item: Item) => void;
  onShare?: (item: Item) => void;
  onAddToFolder?: (item: Item) => void;
  onToggleStar?: (item: Item) => void;
  onTagClick?: (tag: string) => void;
  creatorName?: string;
}

export default memo(function ItemCard({ item: itemProp, onClick, onEdit, onDelete, onRefresh, onShare, onAddToFolder, onToggleStar, onTagClick, creatorName }: ItemCardProps) {
  // Self-poll when enrichment is pending — avoids parent re-renders
  const [localItem, setLocalItem] = useState<Item | null>(null);
  const item = localItem ?? itemProp;

  useEffect(() => {
    setLocalItem(null); // reset when prop changes
  }, [itemProp]);

  // Start self-polling when enrichment is pending
  const lastJsonRef = useRef(JSON.stringify({
    title: itemProp.title, summary: itemProp.summary, imageUrl: itemProp.imageUrl,
    enrichmentStatus: itemProp.enrichmentStatus, tags: itemProp.tags, autoTags: itemProp.autoTags,
  }));
  useEffect(() => {
    if (item.enrichmentStatus !== 'pending') return;
    const interval = setInterval(async () => {
      const { getItem } = await import('../../services/items');
      const updated = await getItem(item.id);
      if (updated) {
        const json = JSON.stringify({
          title: updated.title, summary: updated.summary, imageUrl: updated.imageUrl,
          enrichmentStatus: updated.enrichmentStatus, tags: updated.tags, autoTags: updated.autoTags,
        });
        if (json !== lastJsonRef.current) {
          lastJsonRef.current = json;
          setLocalItem(updated);
        }
        if (updated.enrichmentStatus !== 'pending') clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [item.id, item.enrichmentStatus, itemProp]);

  // Wrap onRefresh to mark pending locally so polling starts without parent re-render
  const handleRefresh = useCallback(async () => {
    if (!onRefresh) return;
    await onRefresh(item);
    setLocalItem({ ...item, enrichmentStatus: 'pending' as const });
  }, [onRefresh, item]);

  const allTags = [...item.tags, ...item.autoTags];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [item.imageUrl]);

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(item);
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.(item);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(item);
  };

  const handleRefreshClick = async () => {
    handleMenuClose();
    await handleRefresh();
  };

  const handleAddToFolder = () => {
    handleMenuClose();
    onAddToFolder?.(item);
  };

  return (
    <Card
      elevation={0}
      sx={{ border: 1, borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
    >
      {onShare && (
        <IconButton
          size="small"
          onClick={handleShare}
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 1,
            bgcolor: 'background.paper',
            opacity: 0.8,
            '&:hover': { opacity: 1, bgcolor: 'background.paper' },
          }}
        >
          <ShareIcon fontSize="small" />
        </IconButton>
      )}
      {(onEdit || onDelete || onRefresh || onAddToFolder) && (
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
            bgcolor: 'background.paper',
            opacity: 0.8,
            '&:hover': { opacity: 1, bgcolor: 'background.paper' },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {onRefresh && (
          <MenuItem onClick={handleRefreshClick}>
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Refresh</ListItemText>
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {onAddToFolder && (
          <MenuItem onClick={handleAddToFolder}>
            <ListItemIcon><PlaylistAddIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Add to Folder</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
      {onToggleStar && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggleStar(item); }}
          sx={{
            position: 'absolute',
            top: 4,
            right: 36,
            zIndex: 1,
            bgcolor: 'background.paper',
            opacity: 0.8,
            '&:hover': { opacity: 1, bgcolor: 'background.paper' },
            color: hasFlag(item.flags, ItemFlags.STARRED) ? 'warning.main' : 'action.disabled',
          }}
        >
          {hasFlag(item.flags, ItemFlags.STARRED) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      )}
      <CardActionArea onClick={() => onClick(item)} sx={{ flexGrow: 1 }}>
        {item.imageUrl && !imgError ? (
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              height={160}
              image={item.imageUrl}
              alt={item.title}
              onError={() => setImgError(true)}
              sx={{ objectFit: 'cover' }}
            />
            {item.enrichmentStatus === 'pending' && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.5)',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0.4 },
                  '50%': { opacity: 1 },
                },
              }}>
                <Typography variant="caption" sx={{
                  color: 'white',
                  animation: 'pulse 2s ease-in-out infinite',
                }}>
                  Enriching...
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              height: 160,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
            }}
          >
            {item.enrichmentStatus === 'pending' ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0 },
                  '50%': { opacity: 1 },
                },
              }}>
                <Box sx={{
                  color: 'primary.main',
                  transform: 'scale(2)',
                  animation: 'pulse 2.5s ease-in-out infinite',
                }}>
                  {typeIcons[item.type]}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{
                  animation: 'pulse 2.5s ease-in-out infinite',
                }}>
                  Enriching...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ color: 'grey.600', transform: 'scale(2.5)' }}>
                {typeIcons[item.type]}
              </Box>
            )}
          </Box>
        )}
        <CardContent>
          <Box
            {...(item.type === 'url' && item.content ? {
              component: 'a',
              href: item.content,
              target: '_blank',
              rel: 'noopener noreferrer',
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            } : {})}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 0.5,
              textDecoration: 'none',
              color: 'text.secondary',
              position: 'relative',
              zIndex: 1,
              ...(item.type === 'url' ? { '&:hover': { color: 'primary.main' } } : {}),
            }}
          >
            {typeIcons[item.type]}
            <Typography variant="caption" color="inherit" noWrap>
              {item.site || item.type}
            </Typography>
          </Box>
          {creatorName && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
              {creatorName}
            </Typography>
          )}
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: hasFlag(item.flags, ItemFlags.READ) ? 500 : 700 }}>
            {item.title && !item.title.startsWith('http')
              ? item.title
              : item.site || item.title}
          </Typography>
          {item.summary && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.summary}
            </Typography>
          )}
          {allTags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {allTags.slice(0, 3).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  onClick={onTagClick ? (e: React.MouseEvent) => { e.stopPropagation(); onTagClick(tag); } : undefined}
                  clickable={!!onTagClick}
                />
              ))}
              {allTags.length > 3 && (
                <Chip label={`+${allTags.length - 3}`} size="small" variant="outlined" />
              )}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
      {item.type === 'url' && item.content && (
        <Box
          component="a"
          href={item.content}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            textDecoration: 'none',
            borderTop: 1,
            borderColor: 'divider',
            position: 'relative',
            zIndex: 1,
            '&:hover': { color: 'primary.main' },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" noWrap>
            {item.site || 'Open link'}
          </Typography>
        </Box>
      )}
    </Card>
  );
});
